import React, { useState, useEffect } from 'react';
import { storageAPI } from '../api';

/**
 * AdminStorageUsage — per-workspace R2 bytes consumed.
 *
 * Two data sources:
 *   - 'db'  — derived from MongoDB size_bytes / file_size at upload time.
 *             Cheap, instant, but slightly approximate.
 *   - 'r2'  — authoritative, computed by listing the R2 bucket. Slow but
 *             exact. Result is cached as a snapshot.
 *
 * The header shows which source is in use and offers a "Refresh from R2"
 * action to regenerate the snapshot.
 */
const AdminStorageUsage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await storageAPI.getUsage();
      setData(res.data);
    } catch (e) {
      setError('Could not load storage usage.');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await storageAPI.refresh();
      setData(res.data);
    } catch (e) {
      setError(
        e?.response?.data?.detail
        || 'Could not refresh from R2. Check storage configuration.'
      );
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return <div style={{ padding: 40 }}><div className="spinner" /></div>;
  if (!data) return <div style={{ padding: 40 }}>{error || 'No data.'}</div>;

  const rows = data.rows || [];
  const total = data.total_bytes || 0;

  return (
    <div data-testid="admin-storage-usage" style={{ display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 1100 }}>
      {/* Header */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <p style={{
            fontFamily: 'Playfair Display, serif', fontSize: 11,
            letterSpacing: '0.32em', textTransform: 'uppercase',
            color: 'var(--kahla-text-light)', margin: '0 0 10px', fontWeight: 300,
          }}>Storage usage</p>
          <h2 style={{
            fontFamily: 'Playfair Display, serif', fontSize: 28, fontWeight: 400,
            color: 'var(--kahla-charcoal)', margin: '0 0 8px', lineHeight: 1.2,
          }}>
            How much R2 each workspace is using.
          </h2>
          <p style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
            fontSize: 16, color: 'var(--kahla-text-light)', margin: 0, lineHeight: 1.65, maxWidth: 640,
          }}>
            Source: {data.source === 'r2' ? (
              <>R2 snapshot from <strong>{formatDate(data.snapshot_at)}</strong>.</>
            ) : (
              <>computed from database (approximate). Refresh below for an authoritative R2 listing.</>
            )}
          </p>
        </div>
        <button
          type="button"
          data-testid="storage-refresh-btn"
          onClick={refresh}
          disabled={refreshing}
          style={{
            background: 'var(--kahla-charcoal)', color: '#fff', border: 'none',
            padding: '11px 22px',
            fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.16em',
            textTransform: 'uppercase',
            cursor: refreshing ? 'wait' : 'pointer',
            opacity: refreshing ? 0.6 : 1,
          }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh from R2'}
        </button>
      </header>

      {error && (
        <div data-testid="storage-error" style={{
          background: '#f8eee9', color: '#7a3a3a', padding: '10px 14px',
          fontSize: 13, fontStyle: 'italic',
        }}>{error}</div>
      )}

      {/* Totals strip */}
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
        <SummaryCard label="Total used" value={data.total_human} testId="storage-total" />
        <SummaryCard label="Workspaces" value={String(rows.length)} testId="storage-workspace-count" />
        <SummaryCard label="Templates" value={data.templates_human || '0 B'} testId="storage-templates" />
      </section>

      {/* Per-workspace table */}
      <section
        data-testid="storage-table"
        style={{ border: '1px solid var(--kahla-border)', background: '#fff' }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1.7fr 1fr 1fr 1fr 1fr 1.1fr',
          alignItems: 'center', gap: 0,
          padding: '14px 18px', borderBottom: '1px solid var(--kahla-cream)',
          fontFamily: 'Playfair Display, serif',
          fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: 'var(--kahla-text-light)',
        }}>
          <span>Workspace</span>
          <span style={{ textAlign: 'right' }}>Library</span>
          <span style={{ textAlign: 'right' }}>Boards</span>
          <span style={{ textAlign: 'right' }}>Inspirations</span>
          <span style={{ textAlign: 'right' }}>Documents</span>
          <span style={{ textAlign: 'right' }}>Total</span>
        </div>

        {rows.length === 0 && (
          <p style={{
            padding: 40, textAlign: 'center',
            fontFamily: 'Cormorant Garamond, Playfair Display, serif',
            fontStyle: 'italic', color: 'var(--kahla-text-light)',
          }}>No storage activity yet.</p>
        )}

        {rows.map((r) => (
          <div
            key={r.owner_id}
            data-testid={`storage-row-${r.owner_id}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '1.7fr 1fr 1fr 1fr 1fr 1.1fr',
              alignItems: 'center', gap: 0,
              padding: '14px 18px',
              borderBottom: '1px solid var(--kahla-cream)',
              fontSize: 13, color: 'var(--kahla-charcoal)',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontFamily: 'Playfair Display, serif', fontSize: 14 }}>
                {r.workspace_name || r.username}
                {r.role === 'admin' && (
                  <span style={{ marginLeft: 8, fontSize: 10, letterSpacing: '0.18em', color: 'var(--kahla-text-light)' }}>
                    · admin
                  </span>
                )}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--kahla-text-light)' }}>
                @{r.username} · {total > 0 ? Math.round((r.total_bytes / total) * 100) : 0}% of total
              </p>
            </div>
            <ByteCell bytes={r.library_bytes} />
            <ByteCell bytes={r.board_bytes} />
            <ByteCell bytes={r.inspiration_bytes} />
            <ByteCell bytes={r.doc_bytes} />
            <ByteCell bytes={r.total_bytes} bold />
          </div>
        ))}
      </section>

      <p style={{
        fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
        fontSize: 13, color: 'var(--kahla-text-light)', margin: 0, lineHeight: 1.65,
      }}>
        Notes — board uploads that came via the library are counted under <em>Library</em>; only direct board uploads appear under <em>Boards</em>. Logo bytes are included only after a refresh from R2.
      </p>
    </div>
  );
};

const SummaryCard = ({ label, value, testId }) => (
  <div data-testid={testId} style={{
    background: '#fff', border: '1px solid var(--kahla-border)',
    padding: '20px 22px',
  }}>
    <p style={{
      fontFamily: 'Playfair Display, serif', fontSize: 10,
      letterSpacing: '0.22em', textTransform: 'uppercase',
      color: 'var(--kahla-text-light)', margin: '0 0 8px', fontWeight: 500,
    }}>{label}</p>
    <p style={{
      fontFamily: 'Playfair Display, serif',
      fontSize: 24, fontWeight: 400,
      color: 'var(--kahla-charcoal)', margin: 0, letterSpacing: '-0.005em',
    }}>{value}</p>
  </div>
);

const ByteCell = ({ bytes, bold }) => (
  <span style={{
    textAlign: 'right',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: bold ? 500 : 400,
    color: bytes > 0 ? 'var(--kahla-charcoal)' : 'rgba(166,152,136,0.55)',
  }}>
    {humanBytes(bytes)}
  </span>
);

function humanBytes(n) {
  n = Math.max(parseInt(n) || 0, 0);
  if (n === 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return `${i === 0 ? n : n.toFixed(1)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (e) { return iso; }
}

export default AdminStorageUsage;
